import { Delete } from '@mui/icons-material';
import {Button, Checkbox, FormControlLabel, Paper} from '@mui/material';

import styles from './RetouchCaseDetail.module.css';

import { AttachmentCategory, Case, ImageState } from '../../../api/caseApiTypes';
import { Right } from '../../../api/userApi';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppRedux';
import { useEitherRights, useRights } from '../../../hooks/useRights';
import { ImagesGridDetailsView } from '../../images/detailsView/ImagesGridDetailsView';
import { CaseAttachments } from '../CaseAttachments';
import { CaseComments } from '../CaseComments';
import { CaseMedia } from '../CaseMedia';
import { CaseStateProgress } from '../CaseStateProgress';
import { CaseTags } from '../CaseTags';
import { CaseTypeDetailComponentProps } from '../CaseTypeDetail';
import { CaseDeadlineDates } from '../formFieldComponents/CaseDeadlineDates';
import { CaseExpansion } from '../layoutComponents/CaseExpansion';
import { CaseFormBlock } from '../layoutComponents/CaseFormBlock';
import { CaseFormButtonContainer } from '../layoutComponents/CaseFormButtonContainer';
import { CaseFormContent } from '../layoutComponents/CaseFormContent';
import { CaseFormSidebar } from '../layoutComponents/CaseFormSidebar';
import { FormFieldsWrapper } from './FormFieldsWrapper';
import { GenericCaseDetailFormFields } from './GenericCaseDetailFormFields';
import { CaseFormSection } from './CaseFormSection';
import { ShowDeliverySections } from './CaseFormLogic';
import { CaseProceedStateButton } from '../CaseProceedStateButton';
import { uploadImage } from '../caseDetailSlice';
import { anyImagesDeleted, isAnyMediaSelected, resetSelectedContext, setSelectedMedia } from '../../images/imagesSlice';
import { useSelectionContext } from '../../images/selectedImagesContext/selectionContextContext';
import { SelectionActionButtonsCard } from '../../images/selectedImagesContext/SelectionActionButtons';
import React, {useState} from "react";

interface Props extends CaseTypeDetailComponentProps {
  caze: Case;
}

export const GenericCaseDetail: React.FC<Props> = ({
  caze,
  isNewCase,
  readonly,
  onDeleteCase,
  onDeleteAttachment,
  onDeleteRelation,
  onCasePartialChange,
  onCreateRelatedCase,
}) => {
  const dispatch = useAppDispatch();
  const isAdmin = useRights(Right.Administration);
  const canUploadImage = useEitherRights([Right.Administration, Right.ManageImage]);

  const canRemoveCase = isAdmin || !readonly;

  const hasDeletedImages = useAppSelector(anyImagesDeleted(caze?.imagePointers.map(x => x.imageId) ?? []));
  const [showDeletedImages, setShowDeletedImages] = useState(false);

  const selectionContext = useSelectionContext();
  const anySelected = useAppSelector(isAnyMediaSelected(selectionContext));
  const clearSelection = () => dispatch(resetSelectedContext(selectionContext));

  const imagesById = useAppSelector(state => state.images.imagesById);

  const visibleImages =
    caze.imagePointers
      ?.map(pointer => imagesById[pointer.imageId])
      .filter(image =>
        image &&
        (showDeletedImages ||
          (image.state !== ImageState.Deleted && !image.removedFromPim))
      ) ?? [];

  const selectedImages = useAppSelector(
    state => state.images.selected[selectionContext]
  );

  const allSelected =
    visibleImages.length > 0 &&
    visibleImages.every(img =>
      selectedImages.some(sel => sel.id === img.id)
    );

  const toggleSelectAll = () => {
    if (allSelected) {
      dispatch(resetSelectedContext(selectionContext));
    } else {
      dispatch(setSelectedMedia({
        selectionContext,
        media: visibleImages,
      }));
    }
  };

  const toggleDeletedImages = () => {
    setShowDeletedImages(!showDeletedImages);
  }

  return (
    <>
      <CaseFormContent>
        <FormFieldsWrapper caze={caze} onChange={onCasePartialChange} isNewCase={isNewCase}>
          <GenericCaseDetailFormFields
            caze={caze}
            readonly={readonly}
            onChange={onCasePartialChange}
            onDeleteAttachment={onDeleteAttachment}
            isNewCase={isNewCase}
          />
        </FormFieldsWrapper>

        {ShowDeliverySections(caze) && (
          <CaseFormSection title="Bilder" caseId={caze.id}>
            <div className={styles.imageActionWrapper}>
              {hasDeletedImages && isAdmin && <FormControlLabel
                className={styles.zeroMargin}
                control={<Checkbox onChange={toggleDeletedImages} checked={showDeletedImages}/>}
                label="Visa även borttagna bilder"
              />
              }
              <FormControlLabel
                className={styles.zeroMargin}
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={!allSelected && anySelected}
                    onChange={toggleSelectAll}
                    disabled={visibleImages.length === 0}
                  />
                }
                label="Markera alla"
              />
            </div>
            <CaseMedia caze={caze} showDeletedImages={showDeletedImages} mediaPointers={caze.imagePointers}
                       caseReadonly={readonly} uploadMediaAction={uploadImage}/>
          </CaseFormSection>
        )}

        {ShowDeliverySections(caze) && canUploadImage && (
          <CaseFormSection title="Original" caseId={caze.id}>
            <CaseAttachments
              caze={caze}
              onDelete={onDeleteAttachment}
              category={AttachmentCategory.OriginalImage}
              right={Right.ManageImage}
              isNewCase={isNewCase}
            />
          </CaseFormSection>
        )}
        <CaseProceedStateButton caze={caze} isNew={isNewCase} />
      </CaseFormContent>
      <CaseFormSidebar>
        <CaseFormBlock>
          <CaseStateProgress caze={caze} isNewCase={isNewCase} />

          <CaseDeadlineDates caze={caze} onChange={onCasePartialChange} readonly={readonly} />

          <CaseTags caze={caze} readonly={isNewCase || readonly} size="small" />

          {canRemoveCase && (
            <CaseFormButtonContainer renderDivider>
              <Button variant="outlined" color="secondary" startIcon={<Delete />} onClick={onDeleteCase} disabled={isNewCase}>
                Ta Bort Beställningen
              </Button>
            </CaseFormButtonContainer>
          )}
        </CaseFormBlock>

        {anySelected && (
          <>
            <SelectionActionButtonsCard />
            <Paper className={styles.imageInformation}>
              <ImagesGridDetailsView onClose={clearSelection} />
            </Paper>
          </>
        )}

        <CaseFormBlock>
          <CaseExpansion title="Kommentarer" hidden={isNewCase} compact>
            <CaseComments caze={caze} />
          </CaseExpansion>
        </CaseFormBlock>
      </CaseFormSidebar>
    </>
  );
};
